// src/App.jsx
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import RequireAuth from './components/RequireAuth';
import RequireAdmin from './components/RequireAdmin';
import RequireInternal from './components/RequireInternal';
import { ChatProvider } from './contexts/ChatContext.jsx';
import ChatAgent from './components/ChatAgent.jsx';
import ChatDM from './components/ChatDM.jsx';
import Login from './views/Login';
import ForgotPassword from './views/ForgotPassword';
import Dashboard from './views/Dashboard';
import Receipts from './views/Receipts';
import Orders from './views/Orders';
import EOrders from './views/EOrders';
import Invoices from './views/Invoices';
import InvoiceDetail from './views/InvoiceDetail';
import Clients from './views/Clients';
import Inventory from './views/Inventory';
import Users from './views/Users';
import Settings from './views/Settings';
import Payments from './views/Payments';
import InvoiceEditor from './views/InvoiceEditor';
import Receive from './views/Receive';
import Items from './views/Items';
import Reports from './views/reports/Reports.jsx';
import Warehouse from './views/Warehouse.jsx';
import WarehouseDesigner from './views/WarehouseDesigner.jsx';
import PickingReplenishment from './views/PickingReplenishment.jsx';
import ShoppingIntegrations from './views/ShoppingIntegrations.jsx';
import ShippingIntegrations from './views/ShippingIntegrations.jsx';
import ShippingFunding from './views/ShippingFunding.jsx';
import WarehouseConfig from './views/WarehouseConfig.jsx';
import AppConfig from './views/AppConfig.jsx';
import ItemBundles from './views/ItemBundles.jsx';
import ShippingPackages from './views/ShippingPackages.jsx';
import ServerStatus from './views/ServerStatus.jsx';
import EditWarehouseFees from './views/EditWarehouseFees.jsx';
import EditReceiptFees from './views/EditReceiptFees.jsx';
import EditOrderFees from './views/EditOrderFees.jsx';
import EditEOrderFees from './views/EditEOrderFees.jsx';
import InvadersView from './views/InvadersView.jsx';
import NokiaSnake from './views/NokiaSnake.jsx';
import SideBar from './components/SideBar';
import TopHeader from './components/TopHeader';
import ToastHost from './components/ToastHost.jsx';
import SettingsModal from './components/SettingsModal.jsx';
import PeriodicInvoicing from './views/PeriodicInvoicing.jsx';
import MonthlyInvoicing from './views/MonthlyInvoicing.jsx';
import WeeklyInvoicing from './views/WeeklyInvoicing.jsx';
import './App.css';
import { clearToasts } from './utils/toast';
import CompanyCalendar from './views/CompanyCalendar.jsx';

// --- THEME PERSISTENCE LOGIC ---//
const THEME_KEYS = [
  'weak_primary_color', 'strong_primary_color', 'primary_color', 'primary_hover',
  'background_dark', 'card_background', 'input_background', 'text_color',
  'error_color', 'success_color', 'evenrow_color', 'button_text_color'
];

function applyTheme(themeData) {
  const defaults = {
    weak_primary_color: '#ffd0b3',
    strong_primary_color: '#ff6200',
    primary_color: '#ff6200',
    primary_hover: '#ff8c00',
    background_dark: '#1a1a1a',
    card_background: '#2c2c2c',
    input_background: '#3a3a3a',
    text_color: '#ffffff',
    error_color: '#ff0000',
    success_color: '#00ff00',
    evenrow_color: '#fdfdfd',
    selected_row_color: '#ffffff',
    button_text_color: '#ffffff'
  };
  const theme = { ...defaults, ...themeData };
  document.documentElement.style.setProperty('--weak-primary-color', theme.weak_primary_color);
  document.documentElement.style.setProperty('--strong-primary-color', theme.strong_primary_color);
  document.documentElement.style.setProperty('--primary-color', theme.primary_color);
  document.documentElement.style.setProperty('--primary-hover', theme.primary_hover);
  document.documentElement.style.setProperty('--background-dark', theme.background_dark);
  document.documentElement.style.setProperty('--card-background', theme.card_background);
  document.documentElement.style.setProperty('--input-background', theme.input_background);
  document.documentElement.style.setProperty('--text-color', theme.text_color);
  document.documentElement.style.setProperty('--error-color', theme.error_color);
  document.documentElement.style.setProperty('--success-color', theme.success_color);
  document.documentElement.style.setProperty('--evenrow-color', theme.evenrow_color);
  document.documentElement.style.setProperty('--selected-row-color', theme.selected_row_color);
  document.documentElement.style.setProperty('--button-text-color', theme.button_text_color);
}

// On startup, check localStorage and apply theme (run once)
(function reapplyThemeFromStorage() {
  try {
    let theme = {};
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.themeData) theme = user.themeData;
    else if (localStorage.getItem('themeData')) theme = JSON.parse(localStorage.getItem('themeData'));
    if (theme && Object.keys(theme).length > 0) {
      applyTheme(theme);
    } else {
      console.log('NO THEME');
    }
  } catch {
    applyTheme({});
  }
})();

function NotFound() {
  return (
    <div style={{ padding: '1rem', color: 'var(--text-color)' }}>
      <h2>404 - Not Found</h2>
    </div>
  );
}

// Helper: read flags from stored user
function getUserFlags() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const adminVal = u.isadmin ?? u.isAdmin ?? u.role ?? u.roleid;
    const isAdmin =
      adminVal === true ||
      adminVal === 1 ||
      String(adminVal).trim().toLowerCase() === 'admin' ||
      String(adminVal) === '1' ||
      String(adminVal).toLowerCase() === 'true';
    const clientVal = u.isclient ?? u.isClient;
    const isClient =
      clientVal === true ||
      clientVal === 1 ||
      String(clientVal) === '1' ||
      String(clientVal).toLowerCase() === 'true';
    return { isAdmin, isClient };
  } catch {
    return { isAdmin: false, isClient: false };
  }
}

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const showNav = location.pathname !== '/' && location.pathname !== '/forgot-password';
  const { isAdmin, isClient } = getUserFlags();

  // Keep previous page as background when viewing /settings so main content doesn't change
  const [bgLocation, setBgLocation] = useState(null);
  useEffect(() => {
    if (location.pathname !== '/settings') setBgLocation(location);
  }, [location]);
  const mainLocation = (location.pathname === '/settings') ? (bgLocation || { ...location, pathname: '/dashboard' }) : location;

  // Clear any lingering toasts when navigating between views
  useEffect(() => {
    clearToasts();
  }, [location.pathname]);

  // ---- Idle session timeout (frontend guard) ----
  useEffect(() => {
    const lastActiveRef = { current: Date.now() };
    const timerRef = { current: null };

    const readSessionTimeoutMs = () => {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}') || {};
        const min = Number(u.sessiontimeout ?? 0);
        if (!Number.isFinite(min) || min <= 0) return 0; // 0 or invalid => no idle auto-logout
        return Math.max(60_000, min * 60 * 1000);
      } catch {
        return 0;
      }
    };

    const performLogout = () => {
      try {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('currentUser');
        localStorage.removeItem('chat.agent.open');
        localStorage.removeItem('chat.dm.open');
        localStorage.removeItem('chat.dm.activeUserId');
      } catch {}
      try { window.dispatchEvent(new CustomEvent('auth:logout')); } catch {}
      window.location.assign('/');
    };

    const tick = () => {
      const ttlMs = readSessionTimeoutMs();
      if (!ttlMs) return;
      const now = Date.now();
      if (now - lastActiveRef.current >= ttlMs) {
        performLogout();
      }
    };

    const restartTimer = () => {
      try { if (timerRef.current) clearInterval(timerRef.current); } catch {}
      timerRef.current = null;
      const ttlMs = readSessionTimeoutMs();
      if (!ttlMs) return;
      const interval = Math.min(Math.max(30_000, ttlMs / 2), 120_000);
      timerRef.current = setInterval(tick, interval);
    };

    const markActive = () => {
      lastActiveRef.current = Date.now();
    };

    const events = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    events.forEach(evt => window.addEventListener(evt, markActive, { passive: true }));
    const onLogin = () => {
      lastActiveRef.current = Date.now();
      restartTimer();
    };
    const onLogout = () => {
      try { if (timerRef.current) clearInterval(timerRef.current); } catch {}
      timerRef.current = null;
    };
    window.addEventListener('auth:login', onLogin);
    window.addEventListener('auth:logout', onLogout);

    // Start timer for current session (if any)
    restartTimer();

    return () => {
      events.forEach(evt => window.removeEventListener(evt, markActive));
      window.removeEventListener('auth:login', onLogin);
      window.removeEventListener('auth:logout', onLogout);
      try { if (timerRef.current) clearInterval(timerRef.current); } catch {}
    };
  }, []);

  return (
    <div className="App" style={{ display: 'flex', flexDirection: 'row', minHeight: '100vh' }}>
      {showNav && <SideBar isAdmin={isAdmin} isClient={isClient} />}
      <div className={`main-content ${showNav ? 'has-topbar' : ''}`}>
        {showNav && <TopHeader />}
        {showNav && <ToastHost />}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Routes location={mainLocation}>
            {/* Public */}
            <Route path="/" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            {/* Shared (client + internal) */}
            <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
            <Route path="/receipts" element={<RequireAuth><Receipts /></RequireAuth>} />
            <Route path="/orders" element={<RequireAuth><Orders /></RequireAuth>} />
            <Route path="/eorders" element={<RequireAuth><EOrders /></RequireAuth>} />
            <Route path="/inventory" element={<RequireAuth><Inventory /></RequireAuth>} />
            <Route path="/calendar" element={<RequireInternal><CompanyCalendar /></RequireInternal>} />
            <Route path="/invoices" element={<RequireAuth><Invoices /></RequireAuth>} />
            <Route path="/invoice/:id" element={<RequireInternal><InvoiceDetail /></RequireInternal>} />
            <Route path="/invoices/new" element={<RequireInternal><InvoiceEditor /></RequireInternal>} />
            <Route path="/invoice/:id/edit" element={<RequireInternal><InvoiceEditor /></RequireInternal>} />
            <Route path="/payments" element={<RequireInternal><Payments /></RequireInternal>} />
            <Route path="/invoices-periodic" element={<RequireInternal><PeriodicInvoicing /></RequireInternal>} />
            <Route path="/invoices-weekly" element={<RequireInternal><WeeklyInvoicing /></RequireInternal>} />
            <Route path="/invoices-monthly" element={<RequireInternal><MonthlyInvoicing /></RequireInternal>} />
            <Route path="/warehouse" element={<RequireInternal><Warehouse /></RequireInternal>} />
            <Route path="/picking-replenishment" element={<RequireInternal><PickingReplenishment /></RequireInternal>} />
            <Route path="/warehouse-config" element={<RequireAdmin><WarehouseConfig /></RequireAdmin>} />
            <Route path="/receive/:receiptid" element={<RequireInternal><Receive /></RequireInternal>} />
            <Route path="/reports" element={<RequireAuth><Reports /></RequireAuth>} />
            <Route path="/invaders" element={<RequireAuth><InvadersView /></RequireAuth>} />
            <Route path="/snake" element={<RequireAuth><NokiaSnake /></RequireAuth>} />
            {/* Internal-only (non-client) */}
            <Route path="/clients" element={<RequireInternal><Clients /></RequireInternal>} />
            <Route path="/items" element={<RequireAuth><Items /></RequireAuth>} />
            <Route path="/bundles" element={<RequireAuth><ItemBundles /></RequireAuth>} />
            {/* Admin-only (also internal-only by definition) */}
            <Route path="/admin" element={<RequireAdmin><Navigate to="/users" replace /></RequireAdmin>} />
            <Route path="/appconfig" element={<RequireAdmin><AppConfig /></RequireAdmin>} />
            <Route path="/users" element={<RequireAdmin><Users /></RequireAdmin>} />
            {/* Settings is now a modal overlay — keep route reserved but not rendered here */}
            <Route path="/warehouses" element={<RequireAdmin><WarehouseDesigner /></RequireAdmin>} />
            <Route path="/warehouse-designer" element={<RequireAdmin><WarehouseDesigner /></RequireAdmin>} />
            <Route path="/shoppingintegrations" element={<RequireAdmin><ShoppingIntegrations /></RequireAdmin>} />
            <Route path="/shippingintegrations" element={<RequireAdmin><ShippingIntegrations /></RequireAdmin>} />
            <Route path="/shipping-funding" element={<RequireAdmin><ShippingFunding /></RequireAdmin>} />
            <Route path="/shipping-packages" element={<RequireAdmin><ShippingPackages /></RequireAdmin>} />
            <Route path="/server-status" element={<RequireAdmin><ServerStatus /></RequireAdmin>} />
            <Route path="/templates/warehousefees" element={<RequireAdmin><EditWarehouseFees /></RequireAdmin>} />
            <Route path="/templates/receiptfees" element={<RequireAdmin><EditReceiptFees /></RequireAdmin>} />
            <Route path="/templates/orderfees" element={<RequireAdmin><EditOrderFees /></RequireAdmin>} />
            <Route path="/templates/eorderfees" element={<RequireAdmin><EditEOrderFees /></RequireAdmin>} />
            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </div>
      {/* Modal overlays rendered globally */}
      <SettingsModal
        open={location.pathname === '/settings'}
        onClose={() => {
          if (bgLocation) navigate(-1);
          else navigate('/dashboard', { replace: true });
        }}
      />
      {showNav && !isClient && <ChatAgent />}
      {showNav && !isClient && <ChatDM />}
    </div>
  );
};

function App() {
  return (
    <ChatProvider>
      <Router>
        <AppContent />
      </Router>
    </ChatProvider>
  );
}

export default App;
